/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateCommentingDisplayConfig, updateDisplayConfig } from 'state/thunks/commenting'
// utils
import { createThemeColors, getHueFromHex, isLight } from 'utils/theme'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as ColorIcon } from 'assets/icons/color.svg'
// content
// import Mode from './mode/mode'
import Fonts from './fonts/fonts'
import UserStatusBadges from './userstatusbadges/userstatusbadges'
import DefaultAvatarPack from './defaultavatarpack/defaultavatarpack'
import Colors from './colors/colors';
import Space from 'material/space/space';
import SetTheme from 'material/settheme/settheme'
import EmojiSelection from './emojiselection/emojiselection'
import ThemePreview from './themepreview/themepreview'
// styles
import injectSheet from 'react-jss'
import styles from './sitethemesection_styles'
import { revertConfigs, setTempCommentingUnitConfig, setTempDisplayConfig, setTempDisplayConfigEmojis } from 'state/slices/commentingv2'
import { isObjectArrayEqual } from 'utils/isObjectArrayEqual'
import Language from './language/language'


/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: SITE THEME SECTION ~~~~~~~~~~ ========== */
const SiteThemeSection = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const sidebarOpen = useSelector(state => state.app.sidebarOpen)
  const { siteUUID } = useSelector(state => state.session);
  const { displayConfig: tempDisplayConfig , emojiConfigs: tempEmojiConfigs, commentingUnitId } = useSelector(state => state.commentingV2.tempCommentingUnitConfig)

  const { displayConfig: savedDisplayConfig, emojiConfigs: savedEmojiConfigs } = useSelector(state => state.commentingV2.commentingUnitConfig)
  const [siteTheme, setSiteTheme] = useState(savedDisplayConfig)

  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpened, setIsOpened] = useState(false)


  // useEffect(() => {
  //   // Revert to the saved display config
  //   if(siteTheme !== savedDisplayConfig && !isOpened) {
  //     setSiteTheme(savedDisplayConfig)
  //   }
  // }, [savedDisplayConfig, isOpened, siteTheme])

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    setIsUpdating(true)
    e.stopPropagation()
    const displayConfigRequest = {displayConfig: {...tempDisplayConfig}, emojiConfigs: [...tempEmojiConfigs] }
    const response = await dispatch(updateCommentingDisplayConfig({ data: displayConfigRequest, commentingUnitId }))
    if(response.payload) setIsOpened(false)
    setIsUpdating(false)
  }, [commentingUnitId, dispatch, tempDisplayConfig, tempEmojiConfigs])

  const handleCancel = useCallback(async() => {
    setIsOpened(false)
    setSiteTheme(savedDisplayConfig)
    dispatch(revertConfigs({
      configName: 'displayConfig'
    }))
    dispatch(revertConfigs({
      configName: 'emojiConfigs'
    }))
  }, [dispatch, savedDisplayConfig])

  const isChanged = useMemo(() => {
    const isDisplayConfigChanged = !isObjectArrayEqual(savedDisplayConfig, tempDisplayConfig)
    const isEmojiConfigsChanged = !isObjectArrayEqual(savedEmojiConfigs, tempEmojiConfigs)
    return isDisplayConfigChanged || isEmojiConfigsChanged
  }, [savedDisplayConfig, savedEmojiConfigs, tempDisplayConfig, tempEmojiConfigs])


  const themeColors = useMemo(() => createThemeColors(!isLight(siteTheme.customBackgroundColor), getHueFromHex(siteTheme.customPrimaryColor), !!siteTheme.enableMonochrome), [siteTheme])

  const setSiteThemeConfig = useCallback((key, value) => {
    setSiteTheme({...siteTheme, [key]: value})
    dispatch(setTempCommentingUnitConfig({
      configName: 'displayConfig',
      data: {...siteTheme, [key]: value}
    }))
  }, [dispatch, siteTheme])

  const setSiteThemeConfigs = useCallback((configObj) => {
    setSiteTheme({...siteTheme, ...configObj})
    dispatch(setTempCommentingUnitConfig({
      configName: 'displayConfig',
      data: {...siteTheme, ...configObj}
    }))
  }, [dispatch, siteTheme])

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
      saveTitle={'Save Changes'}
      icon={<ColorIcon />}
      title='Site Theme'
    />
  );

  return (
    <ExpandableSection
      className={`expandablesectionv2 sitethemesection ${props.classes.sitethemesection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
    >
      <Space gap={16}>
        <Space klass={`stylesection sidebarOpen-${!!sidebarOpen}`} gap={48} direction={'column'}>
          <Colors 
            isConfigOpen={isOpened}
            siteTheme={siteTheme} 
            setSiteThemeConfig={setSiteThemeConfig} 
            setSiteThemeConfigs={setSiteThemeConfigs}
          />
          <Fonts siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <Language siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <UserStatusBadges siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} themeColors={themeColors}  />
          <DefaultAvatarPack siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <EmojiSelection siteTheme={siteTheme} setSiteThemeConfig={setSiteThemeConfig} />
          <SetTheme />
        </Space>
        <Space klass={'themesection'} direction={'column'}>
         <ThemePreview siteTheme={siteTheme} themeColors={themeColors} />
        </Space>
      </Space>
      
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default SiteThemeSection;